/**
 * Overlay Composite Utilities
 *
 * Renders camera overlays and text overlays onto images for export (PDF, etc.)
 */

import type { CameraOverlay, AspectRatio, TextOverlay, TitleNode } from '@/types';
import { presetPositionToCoordinates } from './overlayPositions';

/**
 * Composite overlays onto an image
 * Returns a canvas with the image and overlays rendered together
 */
export async function compositeOverlaysOnImage(
  imageUrl: string,
  overlays: CameraOverlay[],
  aspectRatio: AspectRatio,
  width: number,
  height: number
): Promise<HTMLCanvasElement> {
  // Create canvas
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Load and draw the base image
  const img = await loadImageElement(imageUrl);
  ctx.drawImage(img, 0, 0, width, height);

  // If no overlays, return canvas with just the image
  if (!overlays || overlays.length === 0) {
    return canvas;
  }

  // Render each overlay
  for (const overlay of overlays) {
    try {
      const userScale = overlay.scale || 1;
      const opacity = overlay.opacity !== undefined ? overlay.opacity : 0.8;
      // Scale base size proportionally to image width (80px for ~800px width UI, scale up for PDF)
      // Then apply user's scale setting on top
      const baseSize = 80;
      const pdfScaleFactor = width / 800; // Scale up for higher resolution PDF
      const scaledSize = baseSize * pdfScaleFactor * userScale;

    // Handle push-in and pull-out (4 corner arrows)
    if (overlay.type === 'push-in' || overlay.type === 'pull-out') {
      if (!overlay.imagePath) continue;

      const cornerPositions: Array<'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'> = [
        'top-left',
        'top-right',
        'bottom-right',
        'bottom-left',
      ];

      // Mirror transforms for each corner (flip horizontally/vertically)
      // Assumes base arrow points from top-left toward bottom-right
      const cornerFlips: Record<string, { flipX: boolean; flipY: boolean }> = {
        'top-left': { flipX: false, flipY: false },   // No flip
        'top-right': { flipX: true, flipY: false },   // Flip horizontal
        'bottom-right': { flipX: true, flipY: true }, // Flip both
        'bottom-left': { flipX: false, flipY: true }, // Flip vertical
      };

      // Use absolute URL for overlay image
      const overlayImageUrl = `/overlay-assets/${overlay.imagePath}`;
      const overlayImg = await loadImageElement(overlayImageUrl);

      for (const corner of cornerPositions) {
        const coords = presetPositionToCoordinates(corner);
        const x = coords.x * width;
        const y = coords.y * height;
        const flip = cornerFlips[corner];

        // Draw flipped overlay
        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.translate(x, y);

        // Apply flipping by scaling
        const scaleX = flip.flipX ? -1 : 1;
        const scaleY = flip.flipY ? -1 : 1;
        ctx.scale(scaleX, scaleY);

        ctx.drawImage(
          overlayImg,
          -scaledSize / 2,
          -scaledSize / 2,
          scaledSize,
          scaledSize
        );
        ctx.restore();
      }
    } else if (overlay.type === 'truck-left' || overlay.type === 'truck-right') {
      // Truck overlays: horizontal arrows at bottom, stretched full width
      if (!overlay.imagePath) continue;

      const overlayImageUrl = `/overlay-assets/${overlay.imagePath}`;
      const overlayImg = await loadImageElement(overlayImageUrl);

      const baseHeight = width * 0.08;
      const scaledWidth = width * userScale;
      const scaledHeight = baseHeight * userScale;

      // Position at bottom center
      const x = width / 2;
      const y = height - scaledHeight / 2;

      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.translate(x, y);

      // Mirror for truck-right
      if (overlay.type === 'truck-right') {
        ctx.scale(-1, 1);
      }

      ctx.drawImage(
        overlayImg,
        -scaledWidth / 2,
        -scaledHeight / 2,
        scaledWidth,
        scaledHeight
      );
      ctx.restore();
    } else if (overlay.type === 'pan-left' || overlay.type === 'pan-right') {
      // Pan overlays: curved arrows at bottom, stretched full width
      if (!overlay.imagePath) continue;

      const overlayImageUrl = `/overlay-assets/${overlay.imagePath}`;
      const overlayImg = await loadImageElement(overlayImageUrl);

      const baseHeight = width * 0.08;
      const scaledWidth = width * userScale;
      const scaledHeight = baseHeight * userScale;

      // Position at bottom center
      const x = width / 2;
      const y = height - scaledHeight / 2;

      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.translate(x, y);

      // Mirror for pan-right
      if (overlay.type === 'pan-right') {
        ctx.scale(-1, 1);
      }

      ctx.drawImage(
        overlayImg,
        -scaledWidth / 2,
        -scaledHeight / 2,
        scaledWidth,
        scaledHeight
      );
      ctx.restore();
    } else if (overlay.type === 'tilt-up' || overlay.type === 'tilt-down') {
      // Tilt overlays: vertical curved arrows on right side
      if (!overlay.imagePath) continue;

      const overlayImageUrl = `/overlay-assets/${overlay.imagePath}`;
      const overlayImg = await loadImageElement(overlayImageUrl);

      // Arrow is 200x1080px (aspect ratio: 200/1080)
      const imageAspectRatio = 200 / 1080;
      const baseWidth = height * imageAspectRatio;
      const scaledWidth = baseWidth * userScale;
      const scaledHeight = height * userScale;

      // Position on right side, vertically centered
      const x = width - scaledWidth / 2;
      const y = height / 2;

      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.translate(x, y);

      // Mirror vertically for tilt-down
      if (overlay.type === 'tilt-down') {
        ctx.scale(1, -1);
      }

      ctx.drawImage(
        overlayImg,
        -scaledWidth / 2,
        -scaledHeight / 2,
        scaledWidth,
        scaledHeight
      );
      ctx.restore();
    } else if (overlay.type === 'boom-up' || overlay.type === 'boom-down') {
      // Boom overlays: vertical straight arrows on right side
      if (!overlay.imagePath) continue;

      const overlayImageUrl = `/overlay-assets/${overlay.imagePath}`;
      const overlayImg = await loadImageElement(overlayImageUrl);

      // Arrow is 200x1080px (aspect ratio: 200/1080)
      const imageAspectRatio = 200 / 1080;
      const baseWidth = height * imageAspectRatio;
      const scaledWidth = baseWidth * userScale;
      const scaledHeight = height * userScale;

      // Position on right side, vertically centered
      const x = width - scaledWidth / 2;
      const y = height / 2;

      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.translate(x, y);

      // Mirror vertically for boom-down
      if (overlay.type === 'boom-down') {
        ctx.scale(1, -1);
      }

      ctx.drawImage(
        overlayImg,
        -scaledWidth / 2,
        -scaledHeight / 2,
        scaledWidth,
        scaledHeight
      );
      ctx.restore();
    } else if (overlay.type === 'zoom-in' || overlay.type === 'zoom-out') {
      // Zoom overlays: draw frame with arrows using canvas
      const framePos = overlay.framePosition || { x: 0.5, y: 0.5 };
      const frameSize = overlay.frameSize || 0.5;

      // Calculate frame dimensions
      const frameWidth = width * frameSize;
      const frameHeight = height * frameSize;
      const frameCenterX = framePos.x * width;
      const frameCenterY = framePos.y * height;
      const frameLeft = frameCenterX - frameWidth / 2;
      const frameTop = frameCenterY - frameHeight / 2;

      // Frame corners
      const frameCorners = {
        topLeft: { x: frameLeft, y: frameTop },
        topRight: { x: frameLeft + frameWidth, y: frameTop },
        bottomRight: { x: frameLeft + frameWidth, y: frameTop + frameHeight },
        bottomLeft: { x: frameLeft, y: frameTop + frameHeight },
      };

      // Container corners
      const containerCorners = {
        topLeft: { x: 0, y: 0 },
        topRight: { x: width, y: 0 },
        bottomRight: { x: width, y: height },
        bottomLeft: { x: 0, y: height },
      };

      const strokeColor = overlay.color || '#FFFFFF';
      const strokeWidth = 2 * pdfScaleFactor;
      const arrowSize = 20 * pdfScaleFactor;
      const isZoomIn = overlay.type === 'zoom-in';

      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.strokeStyle = strokeColor;
      ctx.fillStyle = strokeColor;
      ctx.lineWidth = strokeWidth;

      // Draw frame rectangle with solid line
      ctx.setLineDash([]);
      ctx.strokeRect(frameLeft, frameTop, frameWidth, frameHeight);

      // Set dashed lines for arrows
      ctx.setLineDash([5 * pdfScaleFactor, 5 * pdfScaleFactor]);

      // Helper function to draw arrow
      const drawArrow = (fromX: number, fromY: number, toX: number, toY: number) => {
        const angle = Math.atan2(toY - fromY, toX - fromX);
        const arrowX = toX - Math.cos(angle) * arrowSize;
        const arrowY = toY - Math.sin(angle) * arrowSize;

        // Draw line
        ctx.beginPath();
        ctx.moveTo(fromX, fromY);
        ctx.lineTo(toX, toY);
        ctx.stroke();

        // Draw arrow head
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(toX, toY);
        ctx.lineTo(
          arrowX - Math.sin(angle) * arrowSize / 2,
          arrowY + Math.cos(angle) * arrowSize / 2
        );
        ctx.lineTo(
          arrowX + Math.sin(angle) * arrowSize / 2,
          arrowY - Math.cos(angle) * arrowSize / 2
        );
        ctx.closePath();
        ctx.fill();
        ctx.setLineDash([5 * pdfScaleFactor, 5 * pdfScaleFactor]);
      };

      // Draw arrows based on zoom type
      if (isZoomIn) {
        // Zoom In: arrows point FROM corners TO frame
        drawArrow(containerCorners.topLeft.x, containerCorners.topLeft.y, frameCorners.topLeft.x, frameCorners.topLeft.y);
        drawArrow(containerCorners.topRight.x, containerCorners.topRight.y, frameCorners.topRight.x, frameCorners.topRight.y);
        drawArrow(containerCorners.bottomRight.x, containerCorners.bottomRight.y, frameCorners.bottomRight.x, frameCorners.bottomRight.y);
        drawArrow(containerCorners.bottomLeft.x, containerCorners.bottomLeft.y, frameCorners.bottomLeft.x, frameCorners.bottomLeft.y);
      } else {
        // Zoom Out: arrows point FROM frame TO corners
        drawArrow(frameCorners.topLeft.x, frameCorners.topLeft.y, containerCorners.topLeft.x, containerCorners.topLeft.y);
        drawArrow(frameCorners.topRight.x, frameCorners.topRight.y, containerCorners.topRight.x, containerCorners.topRight.y);
        drawArrow(frameCorners.bottomRight.x, frameCorners.bottomRight.y, containerCorners.bottomRight.x, containerCorners.bottomRight.y);
        drawArrow(frameCorners.bottomLeft.x, frameCorners.bottomLeft.y, containerCorners.bottomLeft.x, containerCorners.bottomLeft.y);
      }

      ctx.restore();
    } else {
      // Single overlay (custom position)
      let posX = overlay.position.x;
      let posY = overlay.position.y;

      if (overlay.presetPosition) {
        const coords = presetPositionToCoordinates(overlay.presetPosition);
        posX = coords.x;
        posY = coords.y;
      }

      const x = posX * width;
      const y = posY * height;
      const rotation = overlay.rotation || 0;

      if (overlay.imagePath) {
        // Image-based overlay
        const overlayImageUrl = `/overlay-assets/${overlay.imagePath}`;
        const overlayImg = await loadImageElement(overlayImageUrl);

        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.translate(x, y);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.drawImage(
          overlayImg,
          -scaledSize / 2,
          -scaledSize / 2,
          scaledSize,
          scaledSize
        );
        ctx.restore();
      }
    }
    } catch (error) {
      // Log but continue with other overlays if one fails to load
      console.error('Failed to render overlay:', overlay.type, error);
    }
  }

  return canvas;
}

/**
 * Convert canvas to data URL for PDF embedding
 */
export function canvasToDataURL(canvas: HTMLCanvasElement, quality: number = 0.95): string {
  return canvas.toDataURL('image/jpeg', quality);
}

/**
 * Composite text overlays onto an existing canvas
 */
export function compositeTextOverlays(
  canvas: HTMLCanvasElement,
  textOverlays: TextOverlay[]
): HTMLCanvasElement {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    console.error('Failed to get canvas context for text overlays');
    return canvas;
  }

  const width = canvas.width;
  const height = canvas.height;
  const containerScaleFactor = width / 800;

  textOverlays.forEach((overlay) => {
    const opacity = overlay.opacity !== undefined ? overlay.opacity : 1;
    const color = overlay.color || '#FFFFFF';
    const fontFamily = overlay.fontFamily || 'Arial';
    const alignment = overlay.alignment || 'center';
    const fontWeight = overlay.fontWeight || 'normal';
    const fontStyle = overlay.fontStyle || 'normal';

    const baseFontSize = overlay.fontSize || 24;
    const scaledFontSize = baseFontSize * containerScaleFactor;

    let posX = overlay.position.x;
    let posY = overlay.position.y;

    if (overlay.presetPosition) {
      const presetCoords = presetPositionToCoordinates(overlay.presetPosition);
      posX = presetCoords.x;
      posY = presetCoords.y;
    }

    const absoluteX = posX * width;
    const absoluteY = posY * height;

    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.font = `${fontStyle} ${fontWeight} ${scaledFontSize}px ${fontFamily}`;
    ctx.fillStyle = color;
    ctx.textBaseline = 'middle';

    let textAlign: CanvasRenderingContext2D['textAlign'] = 'center';
    if (alignment === 'left') {
      textAlign = 'left';
    } else if (alignment === 'right') {
      textAlign = 'right';
    }
    ctx.textAlign = textAlign;

    ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
    ctx.shadowBlur = 4;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;

    // Split text into lines and render each line separately for proper multi-line alignment
    const lines = overlay.text.split('\n');
    const lineHeight = scaledFontSize * 1.2;
    const totalHeight = lines.length * lineHeight;
    const startY = absoluteY - (totalHeight / 2) + (lineHeight / 2);

    lines.forEach((line, index) => {
      const y = startY + (index * lineHeight);
      ctx.fillText(line, absoluteX, y);
    });

    ctx.restore();
  });

  return canvas;
}

/**
 * Composite title nodes onto an existing canvas
 * Used for rendering title overlays on storyboard images in PDF export
 */
export function compositeTitleOverlays(
  canvas: HTMLCanvasElement,
  titleNodes: TitleNode[]
): HTMLCanvasElement {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    console.error('Failed to get canvas context for title overlays');
    return canvas;
  }

  const width = canvas.width;
  const height = canvas.height;
  const containerScaleFactor = width / 800;

  titleNodes.forEach((titleNode) => {
    const data = titleNode.data;
    const opacity = data.opacity !== undefined ? data.opacity : 1;
    const color = data.color || '#FFFFFF';
    const fontFamily = data.fontFamily || 'Arial';
    const alignment = data.alignment || 'center';
    const fontWeight = data.fontWeight || 'bold';
    const fontStyle = data.fontStyle || 'normal';

    const baseFontSize = data.fontSize || 48;
    const scaledFontSize = baseFontSize * containerScaleFactor;

    let posX = data.position.x;
    let posY = data.position.y;

    if (data.presetPosition) {
      const presetCoords = presetPositionToCoordinates(data.presetPosition);
      posX = presetCoords.x;
      posY = presetCoords.y;
    }

    const absoluteX = posX * width;
    const absoluteY = posY * height;

    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.font = `${fontStyle} ${fontWeight} ${scaledFontSize}px ${fontFamily}`;
    ctx.fillStyle = color;
    ctx.textBaseline = 'middle';

    let textAlign: CanvasRenderingContext2D['textAlign'] = 'center';
    if (alignment === 'left') {
      textAlign = 'left';
    } else if (alignment === 'right') {
      textAlign = 'right';
    }
    ctx.textAlign = textAlign;

    ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
    ctx.shadowBlur = 4;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;

    // Split text into lines and render each line separately for proper multi-line alignment
    const lines = data.text.split('\n');
    const lineHeight = scaledFontSize * 1.2;
    const totalHeight = lines.length * lineHeight;
    const startY = absoluteY - (totalHeight / 2) + (lineHeight / 2);

    lines.forEach((line, index) => {
      const y = startY + (index * lineHeight);
      ctx.fillText(line, absoluteX, y);
    });

    ctx.restore();
  });

  return canvas;
}

/**
 * Create a white canvas with title overlays for beats without images
 * Used for rendering titles on empty beats in PDF export
 */
export function createWhiteCanvasWithTitles(
  titleNodes: TitleNode[],
  aspectRatio: import('@/types').AspectRatio,
  width: number = 1280
): HTMLCanvasElement {
  const height = aspectRatio === '16:9' ? 720 : aspectRatio === '1:1' ? 1280 : Math.round(1280 * (16 / 9));
  
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }
  
  // Fill with white background
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);
  
  // Composite titles onto the white background
  return compositeTitleOverlays(canvas, titleNodes);
}

/**
 * Load image as HTMLImageElement
 */
function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (error) => {
      console.error(`Failed to load overlay image: ${src}`, error);
      reject(new Error(`Failed to load image: ${src}`));
    };
    img.src = src;
  });
}
